import { tracking } from '@iwoca/frontend-tracking-library';
import { IconEuro } from '@iwoca/icons';
import { Input } from '@iwoca/orion';
import { useLingui } from '@lingui/react/macro';
import { useFormContext } from 'react-hook-form';

import { turnoverSchema } from './TurnoverScreen.schema';
import { trackLeadQuality } from '../../../../hooks/useSignupRouter/trackLeadQuality';
import {
  getAmplitudeDefaultEventProperties,
  SIGNUP_FLOW_DE_MOBILE,
} from '../../../../utils/amplitudeEvents';
import logger from '../../../../utils/logger';
import { TMobileSignupForm } from '../../form/form';
import { useStageFieldTracking } from '../../form/MobileSignupFieldTracking';
import { useMobileSignupPersistence } from '../../form/persistence';
import { useScreenValidation } from '../../form/validation/useScreenValidation';
import { StepLayout } from '../../StepLayout/StepLayout';

// DIT-685. Annual turnover (last 12 months). Registers against the shared mobile
// signup form store (DIT-664); the displayed value is formatted with de-DE
// thousands separators — same logic as the desktop TurnoverInputDE.
export const TurnoverScreen = () => {
  const { t } = useLingui();
  const {
    register,
    watch,
    getValues,
    formState: { errors },
  } = useFormContext<TMobileSignupForm>();
  const { putCompanyStage } = useMobileSignupPersistence();

  const submitBusinessDetails = async () => {
    const values = getValues();
    tracking.ampli.businessDetailsSubmitted({
      ...getAmplitudeDefaultEventProperties(),
      signup_flow: SIGNUP_FLOW_DE_MOBILE,
      annual_turnover: values.last12MonthsTurnover?.replace(/[^0-9]+/g, ''),
      manually_entered_company: Boolean(values.companyEnteredManually),
      contains_crefoid: Boolean(values.crefoId),
    });
    await putCompanyStage(values);

    try {
      await trackLeadQuality();
    } catch (error) {
      logger.error('MobileSignup: trackLeadQuality failed', {
        tags: { team: 'de-introducer' },
        error: error instanceof Error ? error.message : String(error),
      });
    }
  };

  const { validateAndContinue, isSubmitting, submitError } =
    useScreenValidation(turnoverSchema, submitBusinessDetails);
  const { fieldProps } = useStageFieldTracking();

  const amount = watch('last12MonthsTurnover')?.replace(/[^0-9]+/g, '') ?? '';
  const formatted = amount ? parseInt(amount, 10).toLocaleString('de-DE') : '';

  return (
    <StepLayout
      stageTitle={t`Business details`}
      heading={t`Last 12 months’ turnover`}
      subHeading={t`If you have been trading for less than a year, enter your total turnover to date.`}
      onContinue={validateAndContinue}
      loading={isSubmitting}
      submitError={submitError}
      fieldProps={fieldProps}
    >
      <Input
        {...register('last12MonthsTurnover')}
        type="text"
        inputMode="numeric"
        aria-label={t`Last 12 months’ turnover`}
        placeholder={t`e.g. 500,000`}
        iconRight={<IconEuro />}
        value={formatted}
        error={errors.last12MonthsTurnover?.message}
      />
    </StepLayout>
  );
};
